import {
    getFirestore,
    collection,
    query,
    where,
    getDocs,
    getDoc,
    updateDoc,
    deleteDoc,
    doc,
    addDoc,
    onSnapshot,
    arrayUnion,
    serverTimestamp
} from 'firebase/firestore';
import initialModelData from '../components/initialModelData';
import { isEvidenceFullyLinked } from '../utils/MeldUtils';

export const createDisconnectedModel = (modelData) => {
    if (!modelData) return null;

    try {
        const disconnectedModel = JSON.parse(JSON.stringify(modelData));
        if (disconnectedModel.nodes) {
            Object.values(disconnectedModel.nodes).forEach(node => {
                if (node.connections) {
                    node.connections = [];
                }
            });
        }
        disconnectedModel.links = [];
        return disconnectedModel;
    } catch (error) {
        console.error('Error creating disconnected model:', error);
        return null;
    }
};

export const loadStudentModels = async (userId) => {
    const db = getFirestore();
    const studentModelsRef = collection(db, 'studentModels');
    let finalModels = {...initialModelData}; // Start with all initial models

    try {
        const q = query(studentModelsRef, where('userId', '==', userId));
        const querySnapshot = await getDocs(q);

        // Overlay saved models on top of initial models
        querySnapshot.forEach((doc) => {
            if (doc.exists()) {
                const data = doc.data();
                try {
                    if (data.modelData) {
                        const modelName = data.modelName;
                        finalModels[modelName] = JSON.parse(data.modelData);
                    }
                } catch (error) {
                    console.error(`Error processing model data:`, error);
                }
            }
        });

        // For any models without saved data, create disconnected versions
        Object.keys(finalModels).forEach(modelName => {
            if (!querySnapshot.docs.some(doc => doc.data().modelName === modelName)) {
                finalModels[modelName] = createDisconnectedModel(finalModels[modelName]);
            }
        });

        return finalModels;
    } catch (error) {
        console.error('Error loading student models:', error);
        return initialModelData;
    }
};


export const getStudentModel = async (userId, modelName) => {
    try {
        const modelDocRef = doc(getFirestore(), 'studentModels', `${userId}_${modelName}`);
        const modelDoc = await getDoc(modelDocRef);

        if (modelDoc.exists()) {
           return JSON.parse(modelDoc.data().modelData);
        }
        return null;
    } catch (error) {
        console.error(`Error loading model ${modelName} for student ${userId}:`, error);
        return null;
    }
};

export const getUserByEmail = async (email) => {
    try {
        const db = getFirestore();
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('email', '==', email.toLowerCase()));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const userDoc = querySnapshot.docs[0];
            return {
                uid: userDoc.id,
                ...userDoc.data()
            };
        }
        return null;
    } catch (error) {
        console.error('Error getting user by email:', error);
        return null;
    }
};

export const createRoster = async (db, instructorId, rosterName) => {
    try {
        const classCode = await generateUniqueClassCode(db);

        const rosterData = {
            name: rosterName.trim(),
            instructorId,
            students: [],
            classCode,
            createdAt: serverTimestamp()
        };

        const rostersRef = collection(db, 'rosters');
        const docRef = await addDoc(rostersRef, rosterData);
        return docRef.id;
    } catch (error) {
        console.error('Error creating roster:', error);
        throw error;
    }
};

export const deleteRoster = async (db, rosterId) => {
    try {
        await deleteDoc(doc(db, 'rosters', rosterId));
    } catch (error) {
        console.error('Error deleting roster:', error);
        throw error;
    }
};

export const updateRosterStudents = async (db, rosterId, emails) => {
    try {
        const rosterRef = doc(db, 'rosters', rosterId);
        await updateDoc(rosterRef, {
            students: emails.map(email => email.toLowerCase())
        });
    } catch (error) {
        console.error('Error updating roster students:', error);
        throw error;
    }
};

export const validateClassCode = async (classCode) => {
    if (!classCode) return false;

    const db = getFirestore();
    const rostersRef = collection(db, 'rosters');
    const q = query(rostersRef, where('classCode', '==', classCode.toUpperCase()));

    try {
        const snapshot = await getDocs(q);
        return !snapshot.empty;
    } catch (error) {
        console.error('Error validating class code:', error);
        throw error;
    }
};

export const addStudentToRoster = async (classCode, studentEmail) => {
    const db = getFirestore();
    const rostersRef = collection(db, 'rosters');
    const q = query(rostersRef, where('classCode', '==', classCode.toUpperCase()));

    try {
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
            const rosterDoc = snapshot.docs[0];
            const rosterRef = doc(db, 'rosters', rosterDoc.id);
            await updateDoc(rosterRef, {
                students: arrayUnion(studentEmail.toLowerCase())
            });
            return true;
        }
        return false;
    } catch (error) {
        console.error('Error adding student to roster:', error);
        throw error;
    }
};

export const processStudentFile = (fileContent) => {
    return fileContent.split('\n')
        .map(email => email.trim().toLowerCase())
        .filter(email => {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return email !== '' && emailRegex.test(email);
        });
};

export const generateExportData = async (db, roster, currentModel, userEmailToId, selectedStudents = null) => {
    const links = [];
    const filteredStudents = selectedStudents || roster.students || [];

    // First, we need to get the reference model to determine the alphabetical order of models
    let referenceModelData = null;
    try {
        const modelRef = doc(db, 'studentModels', `template_${currentModel}`);
        const modelDoc = await getDoc(modelRef);

        if (modelDoc.exists()) {
            referenceModelData = JSON.parse(modelDoc.data().modelData);
        } else {
            // If no template exists, try to get any student's model as reference
            for (const email of roster.students || []) {
                const studentId = userEmailToId[email.toLowerCase()];
                if (studentId) {
                    const modelDocRef = doc(db, 'studentModels', `${studentId}_${currentModel}`);
                    const studentModelDoc = await getDoc(modelDocRef);
                    if (studentModelDoc.exists()) {
                        referenceModelData = JSON.parse(studentModelDoc.data().modelData);
                        break;
                    }
                }
            }
        }
    } catch (error) {
        console.error(`Error getting reference model: ${error}`);
    }

    // Create model ID to alphabet mapping (still needed for display purposes)
    const modelAlphabetMap = new Map();
    if (referenceModelData && referenceModelData.nodes) {
        const modelNodes = referenceModelData.nodes.filter(node => node.type === 'model');
        modelNodes.forEach((model, index) => {
            const alphabetIndicator = String.fromCharCode(65 + index);
            modelAlphabetMap.set(model.id, alphabetIndicator);
        });
    }

    for (const email of filteredStudents) {
        const studentId = userEmailToId[email.toLowerCase()];
        if (!studentId) {
            console.warn(`No user ID found for student: ${email}`);
            continue;
        }

        const modelDocRef = doc(db, 'studentModels', `${studentId}_${currentModel}`);
        const modelDoc = await getDoc(modelDocRef);

        if (modelDoc.exists()) {
            try {
                const modelData = JSON.parse(modelDoc.data().modelData);
                if (modelData.links && Array.isArray(modelData.links)) {
                    modelData.links.forEach(link => {
                        // Get source and target IDs
                        const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
                        const targetId = typeof link.target === 'object' ? link.target.id : link.target;

                        // Find the corresponding nodes to check their types
                        const sourceNode = modelData.nodes.find(node => node.id === sourceId);
                        const targetNode = modelData.nodes.find(node => node.id === targetId);

                        // Add a flag to indicate if this is an explicit "no effect" link
                        const isExplicitNoEffect = link.value === -1;
                        
                        // For export, we'll use 0 for both NO_LINK and NO_EFFECT_EXPLICIT
                        // but add an additional column to distinguish them
                        const exportValue = isExplicitNoEffect ? 0 : link.value;
                        const linkType = isExplicitNoEffect ? 'no_effect' : 
                                        (link.value === 0 ? 'unlinked' : 
                                        (link.value === 1 ? 'supports' : 
                                        (link.value === 2 ? 'strongly_supports' : 
                                        (link.value === -2 ? 'contradicts' : 'unknown'))));

                        // Use original IDs without alphabetical prefixes
                        links.push({
                            student: email,
                            source: sourceId,
                            target: targetId,
                            value: exportValue,
                            linkType: linkType,
                            isExplicitNoEffect: isExplicitNoEffect
                        });
                    });
                }
            } catch (error) {
                console.error(`Error parsing model data for student ${email}:`, error);
            }
        }
    }

    return links;
};

export const generateCSV = (links, rosterName, currentModel) => {
    // Updated header to include linkType column
    const header = '"student","source","target","value","linkType"\n';
    
    // Updated rows to include linkType
    const rows = links.map(link =>
        `"${link.student}","${link.source}","${link.target}","${link.value}","${link.linkType}"`
    ).join('\n');
    
    const csvContent = header + rows;
    const filename = `${rosterName.replace(/\s+/g, '_')}_${currentModel}_links.csv`;

    return { csvContent, filename };
};

const generateClassCode = () => {
    const allowedChars = 'ABCDEFGHIJKLMNPQRSTUVWXYZ23456789';
    const codeLength = 6;
    let code = '';

    for (let i = 0; i < codeLength; i++) {
        const randomIndex = Math.floor(Math.random() * allowedChars.length);
        code += allowedChars[randomIndex];
    }

    return code;
};

const isClassCodeUnique = async (db, classCode) => {
    const rostersRef = collection(db, 'rosters');
    const q = query(rostersRef, where('classCode', '==', classCode));
    const snapshot = await getDocs(q);
    return snapshot.empty;
};

export const validateExplorerCode = async (code) => {
    if (!code) return false;

    const db = getFirestore();
    const explorerRef = collection(db, 'explorerCodes');
    const q = query(explorerRef, where('code', '==', code.toUpperCase()));

    try {
        const snapshot = await getDocs(q);
        return !snapshot.empty;
    } catch (error) {
        console.error('Error validating explorer code:', error);
        throw error;
    }
};

const generateUniqueClassCode = async (db) => {
    let classCode;
    let isUnique = false;

    while (!isUnique) {
        classCode = generateClassCode();
        isUnique = await isClassCodeUnique(db, classCode);
    }

    return classCode;
};

export const setupFirestoreListeners = (db, instructorId, callbacks) => {
    const BATCH_INTERVAL = 5000;
    const listenerRefs = new Map();
    let batchTimeout = null;
    let pendingUpdates = {
        users: new Map(),
        active: new Set(),
        models: new Map(),
        rosters: []
    };

    const setupListener = (key, ref, handler) => {
        // Only unsubscribe if replacing an existing listener
        if (listenerRefs.has(key)) {
            listenerRefs.get(key)();
        }
        const unsubscribe = onSnapshot(ref, handler, error => {
            console.error(`Error in ${key} listener:`, error);
        });
        listenerRefs.set(key, unsubscribe);
    };

    const processBatchedUpdates = () => {
        const hasUpdates = Object.values(pendingUpdates).some(u =>
            u instanceof Map ? u.size > 0 :
                u instanceof Set ? u.size > 0 :
                    u.length > 0
        );

        if (hasUpdates) {
            if (pendingUpdates.users.size > 0) {
                callbacks.onUsersUpdate?.(
                    Object.fromEntries(pendingUpdates.users),
                    new Set(pendingUpdates.users.keys())
                );
            }
            if (pendingUpdates.active.size > 0) {
                callbacks.onActiveUsersUpdate?.(pendingUpdates.active);
            }
            if (pendingUpdates.models.size > 0) {
                callbacks.onModelsUpdate?.(null, pendingUpdates.models);
            }
            if (pendingUpdates.rosters.length > 0) {
                callbacks.onRostersUpdate?.(pendingUpdates.rosters);
            }

            pendingUpdates = {
                users: new Map(),
                active: new Set(),
                models: new Map(),
                rosters: []
            };
        }
        batchTimeout = null;
    };

    const scheduleBatchedUpdate = () => {
        if (!batchTimeout) {
            batchTimeout = setTimeout(processBatchedUpdates, BATCH_INTERVAL);
        }
    };

    setupListener('users', collection(db, 'users'),
        snapshot => {
            snapshot.docChanges().forEach(change => {
                const email = change.doc.data().email?.toLowerCase();
                if (email) {
                    if (change.type === 'removed') {
                        pendingUpdates.users.delete(email);
                    } else {
                        pendingUpdates.users.set(email, change.doc.id);
                    }
                }
            });
            scheduleBatchedUpdate();
        }
    );

    setupListener('active', collection(db, 'connectedUsers'),
        snapshot => {
            pendingUpdates.active.clear();
            snapshot.forEach(doc => {
                const email = doc.data().email?.toLowerCase();
                if (email) pendingUpdates.active.add(email);
            });
            scheduleBatchedUpdate();
        }
    );

    setupListener('models', collection(db, 'studentModels'),
        snapshot => {
            snapshot.docChanges().forEach(change => {
                try {
                    const [userId, modelName] = change.doc.id.split('_');
                    if (!pendingUpdates.models.has(userId)) {
                        pendingUpdates.models.set(userId, new Set());
                    }
                    const userModels = pendingUpdates.models.get(userId);
                    if (change.type === 'removed') {
                        userModels.delete(modelName);
                    } else {
                        userModels.add(modelName);
                    }
                } catch (error) {
                    console.error('Error processing model:', error);
                }
            });
            scheduleBatchedUpdate();
        }
    );

    setupListener('rosters',
        query(collection(db, 'rosters'), where('instructorId', '==', instructorId)),
        snapshot => {
            // Handle empty collection case
            if (snapshot.empty) {
                pendingUpdates.rosters = [];
                scheduleBatchedUpdate();
                return;
            }

            pendingUpdates.rosters = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                students: doc.data().students?.map(email => email.toLowerCase()) || []
            }));
            scheduleBatchedUpdate();
        }
    );


    return () => {
        listenerRefs.forEach(unsubscribe => unsubscribe());
        listenerRefs.clear();
        if (batchTimeout) {
            clearTimeout(batchTimeout);
        }
    };
};

export const checkMELCompleteness = async (db, studentId, modelName) => {
    try {
        const modelDocRef = doc(db, 'studentModels', `${studentId}_${modelName}`);
        const modelDoc = await getDoc(modelDocRef);

        if (!modelDoc.exists()) {
            return "No MEL";
        }

        const modelData = JSON.parse(modelDoc.data().modelData);
        const evidenceNodes = modelData.nodes.filter(node => node.type === 'evidence');
        
        // Check if all evidence nodes are fully linked
        const allEvidenceFullyLinked = evidenceNodes.every(evidence => 
            isEvidenceFullyLinked(evidence, modelData.nodes, modelData.links)
        );

        return allEvidenceFullyLinked ? "Completed MEL" : "Partial MEL";
    } catch (error) {
        console.error(`Error checking MEL completeness for student ${studentId}:`, error);
        return "Error";
    }
};